import { FC } from "react";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
} from "../../../../../../../../components";
import MuiChip from "../../../../../../../../components/chip/MuiChip";
import {
  convertToUserFormat,
  getFullName,
  getValueOrBlank,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  ParticipantDetail,
  PrimaryManagedStatusKeys,
} from "../../../../../../../createProgram/modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../createProgram/siteCore/SiteCoreKeys";

interface OutreachDetailsProp {
  participantDetail?: ParticipantDetail;
  isCMCProgram?: boolean;
  isTMAssessmentProgram?: boolean;
  isTMLeaderCoachingProgram?: boolean;
}

export const OutreachDetails: FC<OutreachDetailsProp> = ({
  participantDetail,
  isCMCProgram,
  isTMAssessmentProgram,
  isTMLeaderCoachingProgram,
}) => {
  const { commonLabels, fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const { isMobile } = useMediaQueries();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const getLabel = (parent: string, key: string) => {
    return fetchLabel(parent, key);
  };

  const {
    outreach,
    eligibility,
    eligibilityList,
    clientManaged,
    outreachEligible,
    outreachEligibleStatus,
    outreahEligibleStatusList,
    assignedOutreachConsultant,
    releaseDate,
    outreachEligibleDate,
    primaryManagedStatus,
    primaryManagedStatusList,
    secondaryManagedStatus,
    notificationDate,
    notificationSupportDate,
    notificationSupportStatus,
    notificationSupportStatusList,
  } = programSiteCoreKeys;

  const getSecondaryStatusName = () => {
    const primaryStatus = participantDetail?.primaryManagedStatus ?? "";

    if (!primaryStatus) return "";

    const statusMapping = {
      [PrimaryManagedStatusKeys.ClientManagedNoOutreachPermitted]:
        programSiteCoreKeys.noOutreachPermittedList,
      [PrimaryManagedStatusKeys.ClientManagedPendingFurtherActions]:
        programSiteCoreKeys.pendingFurtherActionsList,
      [PrimaryManagedStatusKeys.ClientManagedPendingORE]:
        programSiteCoreKeys.pendingOREList,
    };

    return statusMapping[primaryStatus]
      ? fetchValueByKeyOfDropdown(
          outreach,
          statusMapping[primaryStatus],
          participantDetail?.secondaryManagedStatus ?? "",
        )
      : "";
  };

  return (
    <MuiBox className="content">
      <MuiGrid
        container
        spacing={isMobile ? 0 : 2}
        data-testid="outreach-employer"
      >
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(outreach, eligibility)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={fetchValueByKeyOfDropdown(
              outreach,
              eligibilityList,
              getValueOrBlank(participantDetail?.eligibilityKey),
            )}
          />
        </MuiGrid>

        {participantDetail?.eligibilityKey === clientManaged && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(outreach, primaryManagedStatus)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={fetchValueByKeyOfDropdown(
                outreach,
                primaryManagedStatusList,
                getValueOrBlank(participantDetail?.primaryManagedStatus),
              )}
            />
          </MuiGrid>
        )}

        {participantDetail?.eligibilityKey === clientManaged && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(outreach, secondaryManagedStatus)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={getSecondaryStatusName()}
            />
          </MuiGrid>
        )}

        {participantDetail?.eligibilityKey === outreachEligible && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(outreach, outreachEligibleStatus)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={fetchValueByKeyOfDropdown(
                outreach,
                outreahEligibleStatusList,
                participantDetail?.outreachEligibleStatus ?? "",
              )}
            />
          </MuiGrid>
        )}

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getLabel(outreach, assignedOutreachConsultant)}
            replaceEmptyWithDash
            isValueWithEllipsis
            value={getFullName(
              participantDetail?.assignedOutreachConsultantFirstName ?? "",
              participantDetail?.assignedOutreachConsultantLastName ?? "",
              true,
            )}
          />
        </MuiGrid>

        {!isCMCProgram &&
          !isTMAssessmentProgram &&
          !isTMLeaderCoachingProgram && (
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(outreach, releaseDate)}
                replaceEmptyWithDash
                value={
                  participantDetail?.releaseDate
                    ? convertToUserFormat(
                        participantDetail.releaseDate,
                        userCountryDateFormat,
                      )
                    : ""
                }
              />
            </MuiGrid>
          )}

        {participantDetail?.eligibilityKey === outreachEligible && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(outreach, outreachEligibleDate)}
              replaceEmptyWithDash
              value={convertToUserFormat(
                participantDetail?.outreachEligibleDate,
                userCountryDateFormat,
              )}
            />
          </MuiGrid>
        )}
      </MuiGrid>

      {!isCMCProgram &&
        !isTMAssessmentProgram &&
        !isTMLeaderCoachingProgram && (
          <MuiGrid
            container
            spacing={isMobile ? 0 : 2}
            className="section-grid-container"
          >
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(outreach, notificationDate)}
                replaceEmptyWithDash
                value={convertToUserFormat(
                  participantDetail?.notificationDate,
                  userCountryDateFormat,
                )}
              />
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(outreach, notificationSupportDate)}
                replaceEmptyWithDash
                value={convertToUserFormat(
                  participantDetail?.notificationSupportDate,
                  userCountryDateFormat,
                )}
              />
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(outreach, notificationSupportStatus)}
                replaceEmptyWithDash
                isValueWithEllipsis
                value={fetchValueByKeyOfDropdown(
                  outreach,
                  notificationSupportStatusList,
                  getValueOrBlank(participantDetail?.notificationSupportStatus),
                )}
              />
            </MuiGrid>
          </MuiGrid>
        )}

      {participantDetail?.isContactInADayEnable &&
        !isCMCProgram &&
        !isTMAssessmentProgram &&
        !isTMLeaderCoachingProgram && (
          <MuiGrid container className="section-grid-container">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={commonLabels.selected}
                value={
                  <MuiChip
                    data={[
                      {
                        label: fetchLabel(
                          programSiteCoreKeys?.outreach,
                          programSiteCoreKeys?.contactWithin24Hours,
                        ),
                        value: fetchLabel(
                          programSiteCoreKeys?.outreach,
                          programSiteCoreKeys?.contactWithin24Hours,
                        ),
                      },
                    ]}
                    handleDelete={() => {}}
                    isEditable={false}
                    showMoreARIALabel={commonARIALabel.showNMore}
                    removeChipNameARIALabel={commonARIALabel.removeChipName}
                  />
                }
              />
            </MuiGrid>
          </MuiGrid>
        )}
    </MuiBox>
  );
};
